import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Plus, Printer, Copy, Trash2, Pencil, Package, FileUp, Loader2, FileDown, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LABEL_BADGE = {
  copper: { text: "Copper", bg: "bg-orange-100", fg: "text-orange-800", border: "border-orange-200" },
  copper_customize: { text: "Copper Cust.", bg: "bg-amber-100", fg: "text-amber-900", border: "border-amber-300" },
  india: { text: "India", bg: "bg-emerald-100", fg: "text-emerald-800", border: "border-emerald-200" },
  china: { text: "China", bg: "bg-sky-100", fg: "text-sky-800", border: "border-sky-200" },
};

function LabelTypePicker({ product, onChange }) {
  const type = product.label_type || "copper";
  const conf = LABEL_BADGE[type] || LABEL_BADGE.copper;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid={`label-type-picker-${product.id}`}
          className={`inline-flex items-center gap-1 text-[10.5px] font-medium ${conf.bg} ${conf.fg} border ${conf.border} rounded-full pl-2 pr-1.5 py-0.5 hover:opacity-90 transition-opacity`}
          title="Change label type"
        >
          {conf.text}
          <ChevronDown size={11} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-40">
        {["copper", "copper_customize", "india", "china"].map((k) => (
          <DropdownMenuItem
            key={k}
            disabled={k === type}
            onSelect={() => k !== type && onChange(product.id, k)}
            data-testid={`row-set-type-${product.id}-${k}`}
            className="text-[13px]"
          >
            <span
              className={`inline-block w-2 h-2 rounded-full mr-2 ${LABEL_BADGE[k].bg} border ${LABEL_BADGE[k].border}`}
            />
            {LABEL_BADGE[k].text}
            {k === type && <span className="ml-auto text-[10px] text-zinc-400">current</span>}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function DashboardPage() {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const load = async (query = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/products", { params: query ? { q: query } : {} });
      setProducts(data);
    } catch (e) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/products/${id}`);
      toast.success("Product deleted");
      load(q);
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const { data } = await api.post(`/products/${id}/duplicate`);
      toast.success("Product duplicated");
      navigate(`/edit/${data.id}`);
    } catch {
      toast.error("Failed to duplicate");
    }
  };

  const handleChangeLabelType = async (id, newType) => {
    // Optimistic update — keeps the current filter/search in place.
    setProducts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, label_type: newType } : p))
    );
    try {
      await api.patch(`/products/${id}/label-type`, { label_type: newType });
      toast.success(`Marked as Attro ${newType[0].toUpperCase() + newType.slice(1)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to change label type");
      load(q); // revert to server state
    }
  };

  const handleWordImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith(".docx") && !lower.endsWith(".pdf") && !lower.endsWith(".xlsx")) {
      toast.error("Please choose a .docx, .pdf or .xlsx file");
      return;
    }
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/products/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      toast.success(
        `Imported ${data.length} product${data.length !== 1 ? "s" : ""} from ${file.name}`
      );
      load(q);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Import failed";
      toast.error(msg);
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const visibleProducts = typeFilter === "all"
    ? products
    : products.filter((p) => (p.label_type || "copper") === typeFilter);

  return (
    <div className="px-8 py-7">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1.5">
            Product Library
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-zinc-950">Your saved MRP labels</h1>
          <p className="text-sm text-zinc-500 mt-1.5">
            Search, edit or print any saved product. Auto-generated barcodes from EAN codes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".docx,.pdf,.xlsx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            hidden
            onChange={handleWordImport}
            data-testid="import-word-file-input"
          />
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/import-template.xlsx`}
            download="mrp-labels-template.xlsx"
            data-testid="download-template-link"
            hidden
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-10 gap-2 text-zinc-700 hover:text-zinc-950"
                data-testid="download-template-btn"
              >
                <FileDown size={15} />
                Excel template
                <ChevronDown size={13} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem asChild data-testid="download-template-copper">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/import-template.xlsx?type=copper`}
                  download="mrp-labels-copper-template.xlsx"
                >
                  Attro Copper (2.5 × 2.5 in)
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild data-testid="download-template-copper-customize">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/import-template.xlsx?type=copper_customize`}
                  download="mrp-labels-copper-customize-template.xlsx"
                >
                  Attro Copper Customize (2.5 × 2.5 in)
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild data-testid="download-template-india">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/import-template.xlsx?type=india`}
                  download="mrp-labels-india-template.xlsx"
                >
                  Attro India (3 × 1.5 in)
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild data-testid="download-template-china">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/import-template.xlsx?type=china`}
                  download="mrp-labels-china-template.xlsx"
                >
                  Attro China (3 × 1.5 in)
                </a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="outline"
            className="h-10 border-zinc-300 gap-2"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            data-testid="import-word-btn"
          >
            {importing ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Extracting…
              </>
            ) : (
              <>
                <FileUp size={16} />
                Import Word / PDF / Excel
              </>
            )}
          </Button>
          <Link to="/new" data-testid="create-new-label-btn">
            <Button className="h-10 bg-zinc-950 text-white hover:bg-zinc-800 gap-2">
              <Plus size={16} />
              New label
            </Button>
          </Link>
        </div>
      </div>

      <div className="mb-5 flex items-center gap-3 flex-wrap">
        <div className="relative max-w-md flex-1 min-w-[240px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            data-testid="products-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by product name, EAN or brand…"
            className="pl-9 h-10 bg-white"
          />
        </div>
        <div className="flex items-center gap-1 bg-white border border-zinc-200 rounded-md p-1" data-testid="type-filter-group">
          {[
            { v: "all", label: "All" },
            { v: "copper", label: "Copper" },
            { v: "copper_customize", label: "Copper Cust." },
            { v: "india", label: "India" },
            { v: "china", label: "China" },
          ].map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => setTypeFilter(opt.v)}
              data-testid={`type-filter-${opt.v}`}
              className={`px-3 h-8 rounded text-[12px] font-medium transition-colors ${
                typeFilter === opt.v
                  ? "bg-zinc-950 text-white"
                  : "text-zinc-600 hover:bg-zinc-100"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : visibleProducts.length === 0 ? (
        <div
          className="bg-white border border-dashed border-zinc-300 rounded-md p-14 text-center"
          data-testid="empty-products-state"
        >
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-zinc-100 mb-3">
            <Package size={20} className="text-zinc-500" />
          </div>
          <div className="text-[15px] font-medium text-zinc-900">
            {products.length === 0 ? "No products yet" : `No ${typeFilter} products`}
          </div>
          <div className="text-sm text-zinc-500 mt-1 mb-4">
            {products.length === 0
              ? "Create your first MRP label — it's saved automatically."
              : "Try switching the type filter or create a new label."}
          </div>
          <Link to="/new">
            <Button className="h-9 bg-zinc-950 text-white hover:bg-zinc-800 gap-2">
              <Plus size={15} />
              Create new label
            </Button>
          </Link>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-md overflow-hidden" data-testid="products-list">
          <div className="grid grid-cols-[minmax(0,3fr)_minmax(0,1.4fr)_minmax(0,2fr)_minmax(0,1.4fr)_minmax(0,1.4fr)_minmax(0,1fr)_auto] gap-4 px-5 py-3 border-b border-zinc-200 bg-zinc-50 text-[11px] uppercase tracking-wider text-zinc-500 font-medium">
            <div>Product</div>
            <div>Type</div>
            <div>EAN Code</div>
            <div>Brand</div>
            <div>MRP</div>
            <div>Updated</div>
            <div className="text-right">Actions</div>
          </div>
          {visibleProducts.map((p) => (
            <div
              key={p.id}
              data-testid={`product-row-${p.id}`}
              className="grid grid-cols-[minmax(0,3fr)_minmax(0,1.4fr)_minmax(0,2fr)_minmax(0,1.4fr)_minmax(0,1.4fr)_minmax(0,1fr)_auto] gap-4 px-5 py-3.5 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 transition-colors items-center text-[13px]"
            >
              <div className="min-w-0">
                <div className="font-medium text-zinc-950 truncate">{p.product_name}</div>
                {p.tagline && (
                  <div className="text-[11px] text-zinc-500 truncate mt-0.5">{p.tagline}</div>
                )}
              </div>
              <div>
                <LabelTypePicker product={p} onChange={handleChangeLabelType} />
              </div>
              <div className="text-zinc-700 font-mono text-[12px] truncate">{p.ean_code || "—"}</div>
              <div className="text-zinc-700 truncate">{p.brand_name || "—"}</div>
              <div className="text-zinc-900 font-medium">{p.mrp ? `₹${p.mrp}` : "—"}</div>
              <div className="text-zinc-500 text-[12px]">
                {new Date(p.updated_at).toLocaleDateString()}
              </div>
              <div className="flex items-center gap-1 justify-end">
                <Link to={`/print/${p.id}`} data-testid={`print-product-${p.id}`}>
                  <Button size="sm" variant="ghost" className="h-8 px-2 hover:bg-zinc-100" title="Print">
                    <Printer size={15} />
                  </Button>
                </Link>
                <Link to={`/edit/${p.id}`} data-testid={`edit-product-${p.id}`}>
                  <Button size="sm" variant="ghost" className="h-8 px-2 hover:bg-zinc-100" title="Edit">
                    <Pencil size={15} />
                  </Button>
                </Link>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 px-2 hover:bg-zinc-100"
                  title="Duplicate"
                  data-testid={`duplicate-product-${p.id}`}
                  onClick={() => handleDuplicate(p.id)}
                >
                  <Copy size={15} />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 px-2 hover:bg-red-50 hover:text-red-700"
                      title="Delete"
                      data-testid={`delete-product-${p.id}`}
                    >
                      <Trash2 size={15} />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this product?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently remove <strong>{p.product_name}</strong>. This action
                        cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        data-testid="delete-confirm-btn"
                        className="bg-red-700 hover:bg-red-800"
                        onClick={() => handleDelete(p.id)}
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
